import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    ViewChild,
} from "@angular/core";
import * as THREE from "three";
import { CSG } from "three-csg-ts";
import { ColorCodes } from "@utils/color-constants";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { HalfScreenCanvasConfig } from "@utils/canvas-configuration";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ValidFacesRange } from "@utils/shape-facetype";
import { FaceChoice, ShapeJson } from "../shared/models/shape-selector.model";
import { Choices, ChoiceType, faceDetails, faceLabel, myChoices } from "../shared/models/helper.model";
import { CylinderConfiguration, faceMappings, ShapeSnackbarMessage } from "../shared/models/shape.config.model";
import { ShapeHelperService } from "../services/shape-helper/shape-helper.service";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { SceneManagerService } from "../services/scene-manager/scene-manager.service";
import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { Face, MaterialName } from "../../../../utils/count-calculator/count-calculator.model";
import { TranslateService } from "@ngx-translate/core";
import { FaceType } from "~/src/utils/count-calculator/count-calculator.model";
import { Object3DUserData } from "~/src/utils/shape";
import { DialogService } from "../services/dialog/dialog.service";

interface MeshWithChoice extends THREE.Mesh {
    choice?: string;
}

@Component({
    selector: "app-block-details-component",
    templateUrl: "./block-details-component.component.html",
    styleUrls: ["./block-details-component.component.scss"],
    standalone: false,
})
export class BlockDetailsComponentComponent implements OnDestroy, OnChanges {
    @ViewChild("canvasRef", { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

    private orbitControl!: OrbitControls;
    private objects: THREE.Object3D[] = [];
    public choices = myChoices.choices;
    public selectedChoice: Choices | null = null;
    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    private renderer!: THREE.WebGLRenderer;
    private canvas!: HTMLCanvasElement;
    private animationFrameId: number | null = null;
    private shape: THREE.Mesh | null = null;
    private currentShape!: MeshType;
    public finalMesh!: THREE.Mesh;
    public nodeOrHoleMesh!: MeshWithChoice;
    private raycaster = new THREE.Raycaster();
    private mousePosition = new THREE.Vector2();
    public faceName = faceDetails;
    private directionalLight!: THREE.DirectionalLight;
    private clickedMaterialIndex: number | undefined;

    @Input() shapeJson!: ShapeJson;
    @Input() selectedColor!: THREE.ColorRepresentation;
    @Input() resetSelectedOption!: any;
    @Output() seletedchoice = new EventEmitter<{ [key in faceLabel]: ChoiceType }>();
    @Output() faceColor = new EventEmitter<{}>();

    constructor(
        private snackBar: MatSnackBar,
        private shapeHelperService: ShapeHelperService,
        private dialog: MatDialog,
        private sceneManagerService: SceneManagerService,
        private translate: TranslateService,
        private dialogService: DialogService,
    ) {}

    ngOnInit(): void {
        if (!this.shapeJson) {
            return;
        }

        if (this.resetSelectedOption) {
            this.faceName = this.resetSelectedOption;
        }

        this.currentShape = this.shapeJson.object.name;

        this.initThreeScene();

        window.addEventListener("resize", this.onResize.bind(this));
    }

    ngOnChanges(): void {
        this.selectedColor = this.selectedColor;
        if (this.selectedColor) {
            this.selectedChoice = null;
        }
    }

    private initThreeScene() {
        // Create scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color("#f0fbff");
        this.rendererConfiguration();

        this.cameraConfiguration();
        this.lightConfiguration();
        this.controlsConfiguration();

        this.createShape();

        // Start animation
        this.animate();
    }

    private animate() {
        this.animationFrameId = requestAnimationFrame(() => this.animate());
        //Update controls, required when enableDamping is true
        this.orbitControl.update();

        this.directionalLight?.position.copy(this.camera.position);

        this.renderer.render(this.scene, this.camera);
    }

    private onResize = () => {
        // Get the latest canvas element
        this.canvas = this.canvasRef.nativeElement;

        // Get the parent container's dimensions
        const containerRect = this.canvas.parentElement?.getBoundingClientRect();

        if (containerRect) {
            // Update canvas dimensions to match container
            this.canvas.width = containerRect.width;
            this.canvas.height = containerRect.height;

            // Update camera aspect ratio
            this.camera.aspect = this.canvas.width / this.canvas.height;
            this.camera.updateProjectionMatrix();

            // Resize renderer
            this.renderer.setSize(this.canvas.width, this.canvas.height);
        }
    };

    private rendererConfiguration(): void {
        this.canvas = this.canvasRef.nativeElement;
        // Create renderer
        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
    }

    private cameraConfiguration(): void {
        // Create camera
        this.camera = new THREE.PerspectiveCamera(
            HalfScreenCanvasConfig.perspectiveCameraFov,
            this.canvas.clientWidth / this.canvas.clientHeight,
            HalfScreenCanvasConfig.perspectiveCameraNearField,
            HalfScreenCanvasConfig.perspectiveCameraFarField,
        );

        this.camera.position.set(40, 30, 40);
    }

    private lightConfiguration(): void {
        this.directionalLight = new THREE.DirectionalLight(0xffffff, HalfScreenCanvasConfig.directionalLightValue);
        this.directionalLight.position.copy(this.camera.position);
        this.scene.add(this.directionalLight);

        const ambientLight = new THREE.AmbientLight(ColorCodes.pointLight, HalfScreenCanvasConfig.ambientLightValue);
        this.scene.add(ambientLight);
    }

    private controlsConfiguration(): void {
        this.orbitControl = new OrbitControls(this.camera, this.renderer.domElement);

        const {
            orbitControlMaxDistance,
            orbitControlTarget_X_Coordinate,
            orbitControlTarget_Y_Coordinate,
            orbitControlTarget_Z_Coordinate,
            oribitControlPanSpeed,
            orbitControlDynamicDampingFactor,
        } = HalfScreenCanvasConfig;

        Object.assign(this.orbitControl, {
            maxDistance: orbitControlMaxDistance,
            target: new THREE.Vector3(
                orbitControlTarget_X_Coordinate,
                orbitControlTarget_Y_Coordinate,
                orbitControlTarget_Z_Coordinate,
            ),
            panSpeed: oribitControlPanSpeed,
            enableDamping: true,
            dynamicDampingFactor: orbitControlDynamicDampingFactor,
        });

        this.orbitControl.enableZoom = false;
        this.orbitControl.enablePan = false;
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    // creating shape
    private createShape(): void {
        const isShapePresent = !!this.shapeJson;
        if (isShapePresent) {
            this.currentShape = this.shapeJson.object.name;
            this.shape =
                this.sceneManagerService.createMeshForBlock(
                    this.shapeJson,
                    isShapePresent,
                    this.selectedColor,
                    false,
                ) ?? null;

            if (this.shape) {
                this.finalMesh = this.shape.clone();
                this.scene.add(this.finalMesh);
                this.currentShape = this.shape.name as MeshType;
                this.shapeFaceLabel();
            }
        }
    }

    private shapeFaceLabel(): void {
        if (
            [
                MeshType.CurvedCube,
                MeshType.HalfCylinder,
                MeshType.Cylinder,
                MeshType.Cube,
                MeshType.SquarePyramid,
                MeshType.Prism,
                MeshType.Cone,
                MeshType.HemiSphere,
                MeshType.RightTriangle,
                // MeshType.ConcavePrism,
                MeshType.CurvedCube2,
            ].includes(this.currentShape)
        ) {
            const transparentMesh = this.sceneManagerService.createMeshForBlock(
                this.shapeJson.object.userData["faceMesh"],
                false,
                "",
                false,
            );
            if (transparentMesh) {
                this.objects.push(transparentMesh);
                this.scene.add(transparentMesh);
            }
        }
    }

    @HostListener("dblclick", ["$event"])
    async onDoubleClick(event: MouseEvent): Promise<void> {
        this.seletedchoice.emit(this.faceName);
        const elementBoundingRect = this.renderer.domElement.getBoundingClientRect();

        this.mousePosition.x = ((event.clientX - elementBoundingRect.left) / elementBoundingRect.width) * 2 - 1;
        this.mousePosition.y = -((event.clientY - elementBoundingRect.top) / elementBoundingRect.height) * 2 + 1;

        this.raycaster.setFromCamera(this.mousePosition, this.camera);

        const mesh = this.scene.getObjectByName(this.currentShape) as THREE.Mesh;
        const transparentCube = this.scene.getObjectByName(AuxiliaryObjectType.TransparentCube) as THREE.Mesh;

        const firstIntersection = this.raycaster.intersectObjects(this.scene.children, true)[0];
        const transparentCubeIntersection = this.raycaster.intersectObject(transparentCube, true);
        const meshIntersections = this.raycaster.intersectObject(mesh).filter((intersect) => {
            return intersect.object.name === this.currentShape;
        });

        this.clickedMaterialIndex = meshIntersections.length && meshIntersections[0].face?.materialIndex;

        const materialColorMap: { [key: string]: string } = {};

        if (!meshIntersections.length) {
            this.showSnackbar(this.getTranslation("productDetails.invalidMessage.noIntersectionFound"));
            return; // Exit if there is no mesh intersection
        }

        if (!meshIntersections[0].face) {
            this.showSnackbar(this.getTranslation("productDetails.invalidMessage.invalidFace"));
            return; // Exit if there is no face
        }

        if (firstIntersection.object === mesh) {
            this.showSnackbar(this.getTranslation("productDetails.invalidMessage.clickOnFaceToChangeSelection"));
            return; // Exit if first intersection object is the mesh
        }

        if (
            transparentCubeIntersection[0].object instanceof THREE.Mesh ||
            transparentCubeIntersection[0].object instanceof THREE.LineSegments
        ) {
            if (this.selectedChoice) {
                const { face, object } = meshIntersections[0];
                const material = ((object as THREE.Mesh).material as THREE.MeshStandardMaterial[])[face!.materialIndex];

                if (
                    material?.name === FaceType.Curved ||
                    (this.currentShape === MeshType.RightTriangle && material.name === Face.Front) ||
                    (this.currentShape === MeshType.Prism &&
                        (material.name === Face.Front || material.name === Face.Back))
                ) {
                    this.showSnackbar(this.getTranslation("productDetails.invalidMessage.invalidFace"));
                    return;
                }

                this.handleObject(transparentCube, transparentCubeIntersection[0]);
            }
        }

        // Handle the color change
        if (!this.selectedChoice) {
            const materialIndex = meshIntersections[0].face?.materialIndex;

            if (
                meshIntersections[0].object instanceof THREE.Mesh ||
                meshIntersections[0].object instanceof THREE.LineSegments
            ) {
                let selectedOption;

                if (this.selectedColor) {
                    this.openDialog().subscribe((response) => {
                        if (response) {
                            selectedOption = response;

                            if (materialIndex !== undefined && selectedOption === FaceChoice.singleFace) {
                                if (Array.isArray(this.finalMesh.material)) {
                                    const material = this.finalMesh.material[materialIndex];
                                    if (material instanceof THREE.MeshStandardMaterial) {
                                        material.color.set(this.selectedColor);
                                    }
                                }
                            } else if (selectedOption === FaceChoice.fullface) {
                                if (Array.isArray(this.finalMesh.material)) {
                                    this.finalMesh.material.forEach((material) => {
                                        if (material instanceof THREE.MeshStandardMaterial) {
                                            if (material.name && material.color) {
                                                material.color.set(this.selectedColor);
                                            }
                                        }
                                    });
                                }
                            }

                            if (mesh.material) {
                                if (Array.isArray(mesh.material)) {
                                    // For multiple materials
                                    mesh.material.forEach((material) => {
                                        if (
                                            material instanceof THREE.MeshStandardMaterial &&
                                            material.name &&
                                            MaterialName.includes(material.name) &&
                                            material.color
                                        ) {
                                            materialColorMap[material.name] = material.color.getHexString();
                                        }
                                    });
                                }
                            }
                            this.faceColor.emit(materialColorMap);
                        }
                    });
                }
            }
        }
    }

    private openDialog() {
        const dialogData = {
            componentName: DialogInvokingComponents.ColorCustomization,
        };

        return this.dialogService.openDialog(dialogData, "90%", { disableClose: false }, "672px").afterClosed();
    }

    private handleObject(mesh: THREE.Mesh, intersection: THREE.Intersection): void {
        //range of each face of the shape to be appeared
        const faceRanges: Record<string, number[][]> = {
            [MeshType.HalfCylinder]: [
                [ValidFacesRange.HalfCylinderMinimum, ValidFacesRange.HalfCylinderMaximum],
                [0, 3],
            ],
            [MeshType.HemiSphere]: [[ValidFacesRange.HemiSphereMinimum, ValidFacesRange.HemiSphereMaximum]],
            [MeshType.Cylinder]: [[ValidFacesRange.CylinderMinimum, ValidFacesRange.CylinderMaximum]],
            [MeshType.CurvedCube]: [
                [ValidFacesRange.CurvedCubeMinimum, ValidFacesRange.CurvedCubeMaximum],
                [ValidFacesRange.CurvedCubeSecondRangeMin, ValidFacesRange.CurvedCubeSecondRangeMax],
            ],
            [MeshType.SquarePyramid]: [[ValidFacesRange.SquarePyramidMinimum, ValidFacesRange.SquarePyramidMaximum]],
            [MeshType.Cone]: [[ValidFacesRange.ConeMinimum, ValidFacesRange.ConeMaximum]],
            [MeshType.Prism]: [[ValidFacesRange.PrismRangeMin, ValidFacesRange.PrismRangeMax]],
            [MeshType.Cube]: [[ValidFacesRange.CubeMinimum, ValidFacesRange.CubeMaximum]],
            [MeshType.RightTriangle]: [
                [ValidFacesRange.RightTriangleFirstMinRange, ValidFacesRange.RightTriangleFirstMaxRange],
                [ValidFacesRange.RightTriangleSecondMinRange, ValidFacesRange.RightTriangleSecondMaxRange],
            ],
            // [MeshType.ConcavePrism]: [
            //     [ValidFacesRange.ConcavePrismFirstMinRange, ValidFacesRange.ConcavePrismFirstMaxRange],
            //     [ValidFacesRange.ConcavePrismSecondMinRange, ValidFacesRange.ConcavePrismSecondMaxRange],
            //     [ValidFacesRange.ConcavePrismThirdMinRange, ValidFacesRange.ConcavePrismThirdMaxRange],
            //     [ValidFacesRange.ConcavePrismFourthMinRange, ValidFacesRange.ConcavePrismFourthMaxRange],
            // ],
            [MeshType.CurvedCube2]: [
                [ValidFacesRange.CurvedCube2FirstMinRange, ValidFacesRange.CurvedCube2FirstMaxRange],
                [ValidFacesRange.CurvedCube2SecondMinRange, ValidFacesRange.CurvedCube2SecondMaxRange],
            ],
        };

        const faceIndex = intersection.faceIndex;

        if (faceIndex && faceIndex !== undefined) {
            const ranges = faceRanges[this.currentShape] || [];
            const isValidFace = ranges.some(([min, max]) => faceIndex >= min && faceIndex <= max);

            if (isValidFace) {
                this.handleObjectNodeAndHole(mesh, intersection);
            } else {
                this.showSnackbar(this.getTranslation("productDetails.invalidMessage.invalidFace"));
            }
        }
    }

    private handleObjectNodeAndHole(mesh: THREE.Mesh, intersect: THREE.Intersection): void {
        if (!(mesh.geometry instanceof THREE.BoxGeometry)) {
            return this.showSnackbar(this.getTranslation("productDetails.invalidMessage.objectNotBuffegeometry"));
        }
        const geometry = this.createNodeOrHoleGeometry();
        const material = this.getMaterialForClickedIndex();
        if (!material) return;

        geometry.groups.forEach((group) => (group.materialIndex = this.clickedMaterialIndex!));

        const faceCenters = this.getFaceCenters(mesh.geometry as THREE.BoxGeometry);
        const transparentCubeFaceIndex = this.getFaceIndexFromIntersection(intersect);
        if (transparentCubeFaceIndex === -1)
            return this.showSnackbar(this.getTranslation("productDetails.invalidMessage.faceIndexNotFound"));

        this.nodeOrHoleMesh = new THREE.Mesh(geometry, material);
        this.nodeOrHoleMesh.name = AuxiliaryObjectType.NodeOrHoleMesh;
        this.nodeOrHoleMesh.castShadow = this.nodeOrHoleMesh.receiveShadow = true;
        this.nodeOrHoleMesh.position.copy(faceCenters[transparentCubeFaceIndex]).add(this.finalMesh.position);

        this.adjustPositionForShapes(transparentCubeFaceIndex);
        this.alignMeshWithFaceNormal(intersect.face!.normal);

        switch (this.selectedChoice) {
            case Choices.Node:
            case Choices.Hole:
                this.processNodeOrHoleChoice();
                break;
            case Choices.Plane:
                this.handlePlaneSelection(faceCenters[transparentCubeFaceIndex]);
                break;
            default:
                this.showSnackbar(this.getTranslation("productDetails.invalidMessage.selectYourChoice"));
                break;
        }
    }

    private createNodeOrHoleGeometry(): THREE.CylinderGeometry {
        const geometry = new THREE.CylinderGeometry(
            CylinderConfiguration.radiusTop,
            CylinderConfiguration.radiusBottom,
            CylinderConfiguration.height,
            CylinderConfiguration.radialSegment,
            CylinderConfiguration.heightSegments,
            false,
            CylinderConfiguration.thetaStart,
        );
        geometry.groups.forEach((group) => (group.materialIndex = this.clickedMaterialIndex!));
        return geometry;
    }

    private getMaterialForClickedIndex(): THREE.MeshStandardMaterial | null {
        return (this.finalMesh.material as THREE.MeshStandardMaterial[])[this.clickedMaterialIndex!] || null;
    }

    private getFaceCenters(boxGeometry: THREE.BoxGeometry): THREE.Vector3[] {
        const { width, height, depth } = boxGeometry.parameters;
        return [
            new THREE.Vector3(0, 0, depth / 2), // Front
            new THREE.Vector3(0, 0, -depth / 2), // Back
            new THREE.Vector3(width / 2, 0, 0), // Right
            new THREE.Vector3(-width / 2, 0, 0), // Left
            new THREE.Vector3(0, height / 2, 0), // Top
            new THREE.Vector3(0, -height / 2, 0), // Bottom
        ];
    }

    private getFaceIndexFromIntersection(intersect: THREE.Intersection): number {
        return intersect && intersect.face ? this.getFaceIndexFromNormal(intersect.face.normal) : -1;
    }

    private adjustPositionForShapes(faceIndex: number): void {
        if (this.currentShape === MeshType.RightTriangle) {
            const vertices = this.getRightTriangleVertices(faceIndex);
            if (vertices) {
                const [v1, v2, v3] = vertices;
                this.nodeOrHoleMesh.position.copy(this.shapeHelperService.getCenterForEachFace(v1, v2, v3));
            }
        } else if (this.currentShape === MeshType.Prism) {
            const vertices = this.getPrismVertices(faceIndex);
            if (vertices) {
                const [v1, v2, v3] = vertices;
                this.nodeOrHoleMesh.position.copy(this.shapeHelperService.getCenterForEachFace(v1, v2, v3));
            }
        }
        // else if (this.currentShape === MeshType.ConcavePrism) {
        //     this.adjustConcavePrismPosition(faceIndex);
        // }
    }

    private getRightTriangleVertices(faceIndex: number): THREE.Vector3[] | null {
        if (faceIndex === 0)
            return [new THREE.Vector3(-10, 10, 10), new THREE.Vector3(-10, -10, 10), new THREE.Vector3(10, -10, 10)];
        if (faceIndex === 1)
            return [new THREE.Vector3(-10, 10, -10), new THREE.Vector3(-10, -10, -10), new THREE.Vector3(10, -10, -10)];
        return null;
    }

    private getPrismVertices(faceIndex: number): THREE.Vector3[] | null {
        if (faceIndex === 0)
            return [new THREE.Vector3(-10, -10, 10), new THREE.Vector3(10, -10, 10), new THREE.Vector3(0, 10, 10)];
        if (faceIndex === 1)
            return [new THREE.Vector3(-10, -10, -10), new THREE.Vector3(10, -10, -10), new THREE.Vector3(0, 10, -10)];
        return null;
    }

    private adjustConcavePrismPosition(faceIndex: number): void {
        if (faceIndex === 3) this.nodeOrHoleMesh.position.x -= 3.5; // Back
        if (faceIndex === 5) this.nodeOrHoleMesh.position.y -= 3.5; // Bottom
        if (faceIndex === 0 || faceIndex === 1) this.nodeOrHoleMesh.position.add(new THREE.Vector3(-6.5, -6.5, 0));
    }

    private alignMeshWithFaceNormal(faceNormal: THREE.Vector3): void {
        const axis = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), faceNormal).normalize();
        const angle = Math.acos(new THREE.Vector3(0, 1, 0).dot(faceNormal));
        this.nodeOrHoleMesh.rotation.setFromRotationMatrix(new THREE.Matrix4().makeRotationAxis(axis, angle));
        this.nodeOrHoleMesh.updateMatrix();
    }

    private processNodeOrHoleChoice(): void {
        if (!this.finalMesh.userData[Object3DUserData.nodeOrHole]) {
            this.finalMesh.userData[Object3DUserData.nodeOrHole] = [];
        }

        if (!this.finalMesh.userData[Object3DUserData.wireframe]) {
            this.finalMesh.userData[Object3DUserData.wireframe] = [];
        }

        let combinedMesh!: THREE.Mesh;
        if (this.selectedChoice === Choices.Node) {
            combinedMesh = CSG.union(this.finalMesh, this.nodeOrHoleMesh);
            this.nodeOrHoleMesh.choice = Choices.Node;
        } else if (this.selectedChoice === Choices.Hole) {
            combinedMesh = CSG.subtract(this.finalMesh, this.nodeOrHoleMesh);
            this.nodeOrHoleMesh.choice = Choices.Hole;
            const holeOnlyMesh = CSG.intersect(this.shape!, this.nodeOrHoleMesh); // Get only the hole part
            const wireframeGeometry = new THREE.WireframeGeometry(holeOnlyMesh.geometry);
            const wireframeMaterial = new THREE.LineBasicMaterial({ color: ColorCodes.darkRed });
            const line = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
            this.finalMesh.userData[Object3DUserData.wireframe].push(line);
        }

        if (combinedMesh) {
            combinedMesh.userData[Object3DUserData.nodeOrHole] = [
                ...this.finalMesh.userData[Object3DUserData.nodeOrHole],
                this.nodeOrHoleMesh,
            ];
            combinedMesh.userData[Object3DUserData.wireframe] = this.finalMesh.userData[Object3DUserData.wireframe];
            this.finalMesh.userData[Object3DUserData.wireframe].forEach((wire: THREE.LineSegments) => {
                combinedMesh.add(wire);
            });

            this.scene.remove(this.finalMesh);
            this.finalMesh = combinedMesh;
            this.finalMesh.name = this.currentShape;
            this.scene.add(this.finalMesh);
        }
    }

    /**
     * Handle plane selection
     * @param faceCenter - The center of the selected face
     */
    private handlePlaneSelection(faceCenter: THREE.Vector3): void {
        // Create new mesh with cleaned up nodes/holes
        const updatedMesh = this.cleanupNodesForFace(faceCenter);

        // Update scene and references
        this.scene.remove(this.finalMesh);
        this.finalMesh = updatedMesh;
        this.finalMesh.name = this.currentShape;
        this.scene.add(this.finalMesh);
    }

    /**
     * Clean and remove nodes/holes based on face center
     * @param faceCenter - The center of the selected face
     * @returns Updated mesh with filtered nodes/holes
     */
    private cleanupNodesForFace(faceCenter: THREE.Vector3): THREE.Mesh {
        if (!this.finalMesh?.userData?.[Object3DUserData.nodeOrHole]) {
            return this.shape?.clone()!;
        }

        // Filter nodes/holes directly on the face
        let filteredNodes = this.finalMesh.userData[Object3DUserData.nodeOrHole].filter(
            (node: THREE.Mesh) => !node.position.equals(faceCenter),
        );

        // Filter mesh-specific node positions
        const meshType = this.currentShape;
        const mappings = faceMappings[meshType] || [];

        const mapping = mappings.find((m) => m.position.equals(faceCenter));
        if (mapping) {
            filteredNodes = filteredNodes.filter((node: THREE.Mesh) => {
                return !mapping.nodePositions.some((pos) => pos.equals(node.position));
            });
        }

        // Clone base shape and apply remaining nodes/holes
        let result = this.shape?.clone()!;
        if (!result.userData[Object3DUserData.wireframe]) {
            result.userData[Object3DUserData.wireframe] = [];
        }
        let combinedMesh!: THREE.Mesh;
        filteredNodes.forEach((node: THREE.Mesh & { choice?: string }) => {
            if (node.choice === Choices.Node) {
                combinedMesh = CSG.union(result, node);
            } else if (node.choice === Choices.Hole) {
                combinedMesh = CSG.subtract(result, node);
                const holeOnlyMesh = CSG.intersect(this.shape!, node); // Get only the hole part
                const wireframeGeometry = new THREE.WireframeGeometry(holeOnlyMesh.geometry);
                const wireframeMaterial = new THREE.LineBasicMaterial({ color: ColorCodes.darkRed });
                const line = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
                result.userData[Object3DUserData.wireframe].push(line);
            }
            combinedMesh.userData[Object3DUserData.nodeOrHole] = filteredNodes;
            combinedMesh.userData[Object3DUserData.wireframe] = result.userData[Object3DUserData.wireframe];
            result = combinedMesh;
        });
        result.userData[Object3DUserData.wireframe].forEach((wire: THREE.LineSegments) => {
            result.add(wire);
        });

        return result;
    }

    private getFaceIndexFromNormal(normal: THREE.Vector3): number {
        const tolerance = 0.1;
        const up = new THREE.Vector3(0, 1, 0);

        if (Math.abs(normal.dot(new THREE.Vector3(0, 0, 1)) - 1) < tolerance) {
            this.faceName.left = this.selectedChoice!;
            return 0;
        }
        if (Math.abs(normal.dot(new THREE.Vector3(0, 0, -1)) - 1) < tolerance) {
            this.faceName.right = this.selectedChoice!;
            return 1;
        }
        if (Math.abs(normal.dot(new THREE.Vector3(1, 0, 0)) - 1) < tolerance) {
            this.faceName.front = this.selectedChoice!;
            return 2;
        }

        if (Math.abs(normal.dot(new THREE.Vector3(-1, 0, 0)) - 1) < tolerance) {
            this.faceName.back = this.selectedChoice!;
            return 3;
        }

        if (Math.abs(normal.dot(new THREE.Vector3(0, 1, 0)) - 1) < tolerance) {
            this.faceName.top = this.selectedChoice!;
            return 4;
        }
        if (Math.abs(normal.dot(up.clone().negate()) - 1) < tolerance) {
            this.faceName.bottom = this.selectedChoice!;
            return 5;
        }
        return -1;
    }

    private showSnackbar(message: string): void {
        this.snackBar.open(message, SnackBarConfig.Actions.CLOSE, { duration: SnackBarConfig.Duration.MEDIUM });
    }

    public selectChoice(choice: any): void {
        this.selectedChoice = choice;
    }

    ngOnDestroy() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        this.renderer.dispose();
    }
}
